import React, { FC, forwardRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { SingleValue } from 'react-select';
import styled from 'styled-components';

import LogoutIcon from '@mui/icons-material/Logout';
import LockIcon from '@mui/icons-material/Lock';
import StopIcon from '@mui/icons-material/Stop';
import SyncAltIcon from '@mui/icons-material/SyncAlt';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';


import { platformActions, platformSelectors, useDeepCompareMemo } from '@clatter/platform';
import { DropDown, IconButton } from '@clatter/ui';

import { routesNames } from '../../../routes';
import { themeUISelectors } from '../../../store/UI/theme.slice';
import {
  authSelectors,
  setSelectedTheme,
  stopImpersonating,
  impersonateSelectors,
} from '../../../store';
import { NgTooltip } from '../NgTooltip/NgTooltip';
import { DropdownSelect } from '../DropdownSelect/DropdownSelect';
import { UserProfileIcon } from '../../UserProfileIcon/UserProfileIcon';
import { NavSearchBar } from './NavSearchBar';

import clatterLogo from '../../../../assets/logos/clater_logo.png';
import coBrandDefaultLogo from '../../../../assets/logos/coley_logo.png';

const StyledLogoContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  height: 100%;
  flex-shrink: 0;
  justify-self: start;

  .clickable {
    cursor: pointer;
  }

  img {
    cursor: pointer;
    height: ${({ theme }) => theme.pageHeader.logo.height};
    width: auto;
    object-fit: contain;
    display: block;
  }

  .co-brand-logo {
    max-height: ${({ theme }) => theme.pageHeader.logo.brandHeight};
  }
`;

const StyledSearchCenter = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
`;

const StyledNavBar = styled.nav`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 99;

  display: grid;
  grid-template-columns: 1fr minmax(0, 1400px) 1fr;
  align-items: center;

  gap: ${({ theme }) => theme?.spacing?.default};
  min-height: 60px;
  background: ${({ theme }) => theme?.palette?.white};
  border-bottom: ${({ theme }) => `1px solid ${theme?.palette?.cardBorder}`};
  padding: 0 ${({ theme }) => theme?.spacing?.medium};

  .column {
    display: flex;
    align-items: center;
    gap: 1.25rem;
  }

  .align-right {
    justify-content: flex-end;
    justify-self: end;
  }
`;

const SyledImpersonated = styled.div`
  display: flex;
  height: 100%;
  align-items: center;

  gap: ${({ theme }) => theme.spacing.small};

  p {
    margin: 0;
  }
`;

const ImpersonatedInfo: FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const impersonatedUser = useSelector(impersonateSelectors.getImpersonatedUser);

  if (!impersonatedUser) {
    return null;
  }

  return (
    <SyledImpersonated>
      <p>
        Impersonating: <strong>{impersonatedUser?.email} </strong>
      </p>
      {/* @ts-expect-error */}
      <IconButton
        onClick={(e: any) => {
          e.stopPropagation();
          navigate('/');
          dispatch(stopImpersonating());
        }}
      >
        <NgTooltip title="Stop Impersonate">
          <StopIcon />
        </NgTooltip>
      </IconButton>
    </SyledImpersonated>
  );
};

export const NavBar = forwardRef<HTMLDivElement, any>((props, ref) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const activeUser = useSelector(authSelectors.getUserAuthState)?.user;
  const impersonatedUser = useSelector(impersonateSelectors.getImpersonatedUser);
  const toolInfo = useSelector(platformSelectors.toolInfo.selectToolInfo);
  const appVersion = process.env?.['NX_PUBLIC_APP_VERSION'] || '';

  const [isProfileDropDownOpen, setProfileDropDownOpen] = useState(false);

  const toolsList = useSelector(platformSelectors.tools.selectAllTools);
  const activeToolItem = toolsList.find((tool: any) => tool?.url?.includes(window.location.origin));
  const selectedTheme = useSelector(themeUISelectors.getSelectedTheme) as SingleValue<IDropdownSelectOptionItem>;
  const themesListOptions = useSelector(themeUISelectors.getThemesOptionsList);

  const brandLogo = toolInfo?.data?.companyLogo?.location || coBrandDefaultLogo;
  const isClatterAdmin = useSelector(authSelectors.isClatterAdmin);

  const showThemePicker = false;

  //region METHODS
  const onPasswordChangeClick = async () => {
    try {
      setProfileDropDownOpen(false);
      console.log('Password change clicked');
      //@todo
    } catch (error) {
      console.log('Password change error');
      //@todo
    }
  };

  const onProfileClick = () => {
    setProfileDropDownOpen(false);
    navigate(routesNames.userProfile);
  };

  const onLogoutClick = () => {
    setProfileDropDownOpen(false);
    navigate(routesNames.logout);
  };

  const onImpersonateClick = () => {
    setProfileDropDownOpen(false);
    navigate(routesNames.impersonate);
  };

  const handleThemeChange = (data: any) => {
    dispatch(setSelectedTheme(data));
  };

  const handleLogoClick = () => {
    navigate('/');
  };

  const handleToolChange = (tool: any) => {
    if (tool?.slug) {
      dispatch(platformActions.persisted.set({ type: 'lastUsedTool', data: tool.slug }));
    }
  };
  //endregion

  const profileDropDownOptions = useDeepCompareMemo(() => {
    return [
      {
        name: 'Profile',
        icon: <AccountCircleIcon />,
        clickEvent: onProfileClick,
      },
      (isClatterAdmin || impersonatedUser) && {
        name: 'Impersonate',
        icon: <SyncAltIcon />,
        clickEvent: onImpersonateClick,
      },
      {
        name: 'Change Password',
        icon: <LockIcon />,
        clickEvent: onPasswordChangeClick,
      },
      {
        name: 'Logout',
        icon: <LogoutIcon />,
        clickEvent: onLogoutClick,
      },
    ].filter((item) => item);
  }, [isClatterAdmin, impersonatedUser, activeUser]);

  //region EFFECTS
  const toggleUserProfileDropdown = () => {
    setProfileDropDownOpen((prev) => !prev);
  };
  //endregion

  return (
    <StyledNavBar ref={ref} {...props}>
      <StyledLogoContainer>
        {toolsList.length > 1 ? (
          <DropDown
            hideActive
            options={toolsList as any}
            value={activeToolItem}
            noValueLabel="Select a Tool"
            minDropdownWidth="250px"
            triggerComponent={(<img src={clatterLogo} alt="clatter logo" />) as any}
            onChange={handleToolChange as any}
          />
        ) : (
          <img src={clatterLogo} onClick={() => handleLogoClick()} alt="clatter logo" />
        )}

        <div style={{ width: '1px', height: '20px', background: '#D1D5DB' }}></div>

        {brandLogo && (
          <img className="co-brand-logo" src={brandLogo} onClick={() => handleLogoClick()} alt="brand logo" />
        )}
      </StyledLogoContainer>

      <StyledSearchCenter>
        <NavSearchBar />
      </StyledSearchCenter>

      <div className="column align-right">
        {showThemePicker && (
          <div style={{ minWidth: '130px' }}>
            <DropdownSelect value={selectedTheme} options={themesListOptions} onChange={handleThemeChange} />
          </div>
        )}

        <ImpersonatedInfo />

        <UserProfileIcon
          testId="user-circle"
          appVersion={appVersion}
          user={activeUser}
          onClose={() => setProfileDropDownOpen(false)}
          isOpen={isProfileDropDownOpen}
          dropdownOptions={profileDropDownOptions as any}
          handleUserIconClick={toggleUserProfileDropdown}
        />
      </div>
    </StyledNavBar>
  );
});
